import { updateCache } from "@/services/backend/_lib/update-cache";
import { DocumentState } from "@/services/backend/documents/types/state";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { backendApi } from "@/services/backend/api";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import {
  Position,
  WorkOrder,
} from "@/services/backend/htz/work-order/types/work-order";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import { StructuralAssessment } from "@/services/backend/htz/inspection/types/structural-assessment";
import { VisualAssessment } from "@/services/backend/htz/inspection/types/visual-assessment";
import { Photo } from "@/services/backend/htz/inspection/types/photo";
import { Kind } from "@/services/backend/samples/types/kind";
import { WaterKind } from "@/services/backend/samples/types/water-sample";

// invalidatesPosition invalidates the position of a request changing it, and
// its work order, whose readiness derives from its positions.
const invalidatesPosition = (
  _result: unknown,
  _error: unknown,
  request: { id: string; positionId: string },
) => [
  { type: "HtzWorkOrderPosition" as const, id: request.positionId },
  { type: "HtzWorkOrderPosition" as const, id: "LIST" },
  { type: "HtzWorkOrder" as const, id: request.id },
];

// The samples of the microbial assessments are created with them, so the
// work order invalidates the tags of the samples, too.
const workOrderApi = backendApi
  .enhanceEndpoints({
    addTagTypes: [
      "SamplesAirMicrobialSampleSet",
      "SamplesDustDensitySample",
      "SamplesSurfaceMicrobialSampleSet",
      "SamplesWaterSample",
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      htzWorkOrderCreate: build.mutation<WorkOrder, CreateRequest>({
        query: (request) => ({
          url: `/htz/work-order`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrder", id: request.id },
          { type: "HtzWorkOrder", id: "LIST" },
        ],
      }),
      htzWorkOrderUpdateWorkOrderState: build.mutation<
        WorkOrder,
        UpdateWorkOrderStateRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/work-order-state`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrder", id: request.id },
          "HtzWorkOrder",
        ],
      }),
      // The position endpoints return the position. They invalidate the work
      // order, since its commitment totals and readiness derive from its
      // positions.
      htzWorkOrderCreatePosition: build.mutation<
        Position,
        CreatePositionRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/position`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrderPosition", id: "LIST" },
          { type: "HtzWorkOrder", id: request.id },
        ],
      }),
      htzWorkOrderPatchPosition: build.mutation<Position, PatchPositionRequest>(
        {
          query: (request) => ({
            url: `/htz/work-order/position`,
            method: "PATCH",
            body: request,
          }),
          invalidatesTags: (_result, _e, request) => [
            { type: "HtzWorkOrderPosition", id: request.positionId },
            { type: "HtzWorkOrderPosition", id: "LIST" },
            { type: "HtzWorkOrder", id: request.id },
          ],
        },
      ),
      // Assigning an air handling unit may create the air handling unit data
      // of the work order, or delete the one no position references anymore.
      htzWorkOrderAssignPositionAhu: build.mutation<
        Position,
        AssignPositionAhuRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/position/ahu`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrderPosition", id: request.positionId },
          { type: "HtzWorkOrderPosition", id: "LIST" },
          { type: "HtzWorkOrder", id: request.id },
          "HtzAirHandlingUnitData",
        ],
      }),
      // The assessments of a position are changed on their own.
      htzWorkOrderAddAssessment: build.mutation<Position, AddAssessmentRequest>(
        {
          query: (request) => ({
            url: `/htz/work-order/position/assessment`,
            method: "POST",
            body: request,
          }),
          invalidatesTags: invalidatesPosition,
        },
      ),
      // The sample is created with the assessment, in one transaction.
      htzWorkOrderAddMicrobialAssessmentWithSample: build.mutation<
        Position,
        AddMicrobialAssessmentWithSampleRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/position/assessment/microbial`,
          method: "POST",
          body: request,
        }),
        invalidatesTags: (result, error, request) => [
          ...invalidatesPosition(result, error, request),
          "SamplesSampleViewModel",
          "SamplesAirMicrobialSampleSet",
          "SamplesDustDensitySample",
          "SamplesSurfaceMicrobialSampleSet",
          "SamplesWaterSample",
        ],
      }),
      htzWorkOrderPatchStructuralAssessment: build.mutation<
        Position,
        PatchStructuralAssessmentRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/position/assessment/structural`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesPosition,
      }),
      htzWorkOrderPatchVisualAssessment: build.mutation<
        Position,
        PatchVisualAssessmentRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/position/assessment/visual`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesPosition,
      }),
      htzWorkOrderPatchPhoto: build.mutation<Position, PatchPhotoRequest>({
        query: (request) => ({
          url: `/htz/work-order/position/assessment/photo`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: invalidatesPosition,
      }),
      htzWorkOrderRemoveAssessment: build.mutation<
        Position,
        RemoveAssessmentRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/position/assessment`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: invalidatesPosition,
      }),
      htzWorkOrderSign: build.mutation<null, SignRequest>({
        query: (request) => {
          const body = new FormData();
          body.append("id", request.id);
          body.append("signature", request.signature);
          body.append("dataFormat", request.dataFormat);
          body.append("nameOfSigner", request.nameOfSigner);
          return {
            url: `/htz/work-order/signature`,
            method: "PATCH",
            body,
            formData: true,
          };
        },
      }),
      htzWorkOrderShow: build.query<WorkOrder, ShowRequest>({
        query: (request) => ({
          url: `/htz/work-order`,
          method: "GET",
          params: request,
        }),
        providesTags: providesEntity("HtzWorkOrder"),
      }),
      htzWorkOrderList: build.query<Lst<WorkOrder>, ListRequest>({
        query: (request) => ({
          url: `/htz/work-order/list`,
          method: "GET",
          params: {
            request: btoa(JSON.stringify(request)),
          },
        }),
        providesTags: (result) => providesList(result?.items, "HtzWorkOrder"),
      }),
      htzWorkOrderListPosition: build.query<Lst<Position>, ListPositionRequest>(
        {
          query: (request) => ({
            url: `/htz/work-order/position/list`,
            method: "GET",
            params: {
              request: btoa(JSON.stringify(request)),
            },
          }),
          providesTags: (result) =>
            providesList(result?.items, "HtzWorkOrderPosition"),
        },
      ),
      htzWorkOrderDelete: build.mutation<null, DeleteRequest>({
        query: (request) => ({
          url: `/htz/work-order`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["HtzWorkOrder", "HtzWorkOrderPosition"],
      }),
      htzWorkOrderDeletePosition: build.mutation<null, DeletePositionRequest>({
        query: (request) => ({
          url: `/htz/work-order/position`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: ["HtzWorkOrder", "HtzWorkOrderPosition"],
      }),
      htzWorkOrderPhotoDocumentationUploadImage: build.mutation<
        null,
        UploadImageRequest
      >({
        query: (request) => {
          const body = new FormData();
          body.append("id", request.id);
          body.append("positionId", request.positionId);
          body.append("photoId", request.photoId);
          body.append("fileId", request.fileId);
          body.append("file", request.file);
          return {
            url: `/htz/work-order/photo-documentation/photos`,
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrderPosition", id: request.positionId },
          "HtzWorkOrderPosition",
        ],
      }),
      htzWorkOrderPhotoDocumentationShowImage: build.query<
        { objectURL: string },
        ShowImageRequest
      >({
        ...revokeObjectURLOptions(),
        query: (request) => ({
          url: `/htz/work-order/photo-documentation/photos`,
          method: "GET",
          params: request,
          responseHandler: objectURLResponseHandler,
          // Keep the image out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzWorkOrderPhotoDocumentationDeleteImage: build.mutation<
        null,
        DeleteImageRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/photo-documentation/photos`,
          method: "DELETE",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrderPosition", id: request.positionId },
          "HtzWorkOrderPosition",
          "HtzWorkOrder",
        ],
      }),
      htzWorkOrderPdfPreview: build.query<
        { objectURL: string },
        PreviewPDFRequest
      >({
        ...revokeObjectURLOptions(),
        query: (request) => ({
          url: `/htz/work-order`,
          method: "GET",
          params: request,
          headers: { Accept: "application/pdf" },
          responseHandler: objectURLResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzWorkOrderPdfDownload: build.mutation<
        { filename: string; objectURL: string },
        DownloadPDFRequest
      >({
        query: (request) => ({
          url: `/htz/work-order`,
          method: "GET",
          params: { ...request, download: true },
          headers: { Accept: "application/pdf" },
          responseHandler: downloadResponseHandler,
          // Keep the PDF out of the browser's HTTP cache.
          cache: "no-store",
        }),
      }),
      htzWorkOrderPatch: build.mutation<WorkOrder, PatchRequest>({
        query: (request) => ({
          url: `/htz/work-order`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "HtzWorkOrder", id: request.id },
          { type: "HtzWorkOrder", id: "LIST" },
        ],
      }),
      htzWorkOrderResetReferenceNumber: build.mutation<
        WorkOrder,
        ResetReferenceNumberRequest
      >({
        query: (request) => ({
          url: `/htz/work-order/reset-reference-number`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrder", id: request.id },
          { type: "HtzWorkOrder", id: "LIST" },
        ],
      }),
      htzWorkOrderUpdateState: build.mutation<WorkOrder, UpdateStateRequest>({
        query: (request) => ({
          url: `/htz/work-order/state`,
          method: "PATCH",
          body: request,
        }),
        invalidatesTags: (_result, _e, request) => [
          { type: "HtzWorkOrder", id: request.id },
          "HtzWorkOrder",
        ],
      }),
    }),
  });

export const {
  useHtzWorkOrderCreateMutation,
  useHtzWorkOrderUpdateWorkOrderStateMutation,
  useHtzWorkOrderCreatePositionMutation,
  useHtzWorkOrderPatchPositionMutation,
  useHtzWorkOrderAssignPositionAhuMutation,
  useHtzWorkOrderAddAssessmentMutation,
  useHtzWorkOrderAddMicrobialAssessmentWithSampleMutation,
  useHtzWorkOrderPatchStructuralAssessmentMutation,
  useHtzWorkOrderPatchVisualAssessmentMutation,
  useHtzWorkOrderPatchPhotoMutation,
  useHtzWorkOrderRemoveAssessmentMutation,
  useHtzWorkOrderSignMutation,
  useHtzWorkOrderShowQuery,
  useHtzWorkOrderPatchMutation,
  useHtzWorkOrderResetReferenceNumberMutation,
  useLazyHtzWorkOrderShowQuery,
  useHtzWorkOrderListQuery,
  useHtzWorkOrderListPositionQuery,
  useLazyHtzWorkOrderListPositionQuery,
  useHtzWorkOrderDeleteMutation,
  useHtzWorkOrderDeletePositionMutation,
  useHtzWorkOrderPhotoDocumentationUploadImageMutation,
  useHtzWorkOrderPhotoDocumentationShowImageQuery,
  useHtzWorkOrderPhotoDocumentationDeleteImageMutation,
  useLazyHtzWorkOrderPdfPreviewQuery,
  useHtzWorkOrderPdfDownloadMutation,
  useHtzWorkOrderUpdateStateMutation,
} = workOrderApi;

/**
 * CreateRequest identifies a new work order. Everything else is a default
 * or set with a {@link PatchRequest}.
 */
export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged, null clears it.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
  executingBranchId?: string;
  beginDate?: string | null;
  endDate?: string | null;
  teamLeader?: string;
  internalComment?: string;
  address?: Address;
  contact?: Contact;
  representative?: Representative;
}

/**
 * UpdateWorkOrderStateRequest moves the work order to another
 * workOrderState. The document state follows it, recorded for the user in
 * stateSetBy - the backend policy rejects any other user.
 */
export interface UpdateWorkOrderStateRequest {
  id: string;
  workOrderState: FulfillmentState;
  stateSetBy: string;
}

/**
 * PositionPatch holds the fields of a position that may be patched. An
 * omitted key leaves the field unchanged. The totals are derived by the
 * backend.
 */
export interface PositionPatch {
  completed?: boolean;
  title?: string;
  description?: string;
  unitCount?: number;
  unitPrice?: number;
  unitHours?: number;
  structuralInspectionRequired?: boolean;
  visualInspectionRequired?: boolean;
  microbialInspectionRequired?: boolean;
  photoDocumentationRequired?: boolean;
}

// CreatePositionRequest creates the position `positionId` on the work order
// `id`, prefilled with the patch, e.g. from a catalog item.
export interface CreatePositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

// PatchPositionRequest updates the position `positionId` of the work order
// `id`.
export interface PatchPositionRequest extends PositionPatch {
  id: string;
  positionId: string;
}

// AssignPositionAhuRequest assigns the air handling unit `ahuId` to the
// position `positionId` of the work order `id`. The zero UUID removes it.
export interface AssignPositionAhuRequest {
  id: string;
  positionId: string;
  ahuId: string;
}

export interface SignRequest {
  id: string;
  signature: string;
  dataFormat: string;
  nameOfSigner: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  workOrderState?: Filter<FulfillmentState>;
  orderBy?: OrdrBy<WorkOrderOrderBy>[];
  executingBranch?: Filter<string>;
  date?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export type WorkOrderOrderBy =
  | "workOrderState"
  | "beginDate"
  | "endDate"
  | "teamLeader"
  | "createdAt"
  | "updatedAt"
  | "stateSetAt";

export interface ListPositionRequest {
  workOrder?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export interface DeletePositionRequest {
  id: string;
  workOrderId: string;
}

export enum WorkOrderDocument {
  ALL = "all",
  WORK_ORDER = "workOrder",
}

export interface UploadImageRequest {
  id: string;
  positionId: string;
  photoId: string;
  fileId: string;
  file: File;
}

export interface ShowImageRequest {
  id: string;
  fileId: string;
}

export interface DeleteImageRequest {
  id: string;
  positionId: string;
  photoId: string;
  fileId: string;
}

export interface PreviewPDFRequest {
  workOrderId: string;
  document: WorkOrderDocument;
}

export interface DownloadPDFRequest {
  workOrderId: string;
  document: WorkOrderDocument;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export const updateHtzWorkOrderCache = updateCache(
  workOrderApi,
  "htzWorkOrderShow",
);

// AddAssessmentRequest adds the assessment `assessmentId` of the `type` for
// the component to the position. `structural` and `visual` prefill it,
// e.g. from a catalog item.
export interface AddAssessmentRequest {
  id: string;
  positionId: string;
  type: "microbial" | "photo" | "structural" | "visual";
  assessmentId: string;
  componentId: string;
  structural?: Partial<
    Pick<
      StructuralAssessment,
      "requirement" | "deficiency" | "recommendation" | "result"
    >
  >;
  visual?: Partial<
    Pick<VisualAssessment, "deficiency" | "recommendation" | "result">
  >;
  // sampleKind and sampleId assign an existing sample to a new microbial
  // assessment, e.g. an outdoor air sample taken for several air handling
  // units.
  sampleKind?: Kind;
  sampleId?: string;
}

// AddMicrobialAssessmentWithSampleRequest adds the microbial assessment
// `assessmentId` to the position and creates the sample `sampleId` it is
// taken with. `waterKind` is required for water samples only.
export interface AddMicrobialAssessmentWithSampleRequest {
  id: string;
  positionId: string;
  assessmentId: string;
  componentId: string;
  sampleId: string;
  sampleKind: Kind;
  waterKind?: WaterKind;
  number: string;
  location: string;
  spot: string;
}

interface AssessmentRequest {
  id: string;
  positionId: string;
  assessmentId: string;
}

export type PatchStructuralAssessmentRequest = AssessmentRequest &
  Partial<Omit<StructuralAssessment, "id" | "ahuId">>;

export type PatchVisualAssessmentRequest = AssessmentRequest &
  Partial<Omit<VisualAssessment, "id" | "ahuId">>;

// PatchPhotoRequest patches a photo. Its file is uploaded with
// {@link UploadImageRequest}, it cannot be patched.
export type PatchPhotoRequest = AssessmentRequest &
  Partial<Pick<Photo, "componentId" | "comment" | "conductedAt">>;

export type RemoveAssessmentRequest = AssessmentRequest;

/**
 * workOrderPositionsRequest is the request listing the positions of the work
 * order. Every reader of the positions uses it, so they share one cache
 * entry, which {@link updateHtzWorkOrderPositionCache} updates.
 */
export function workOrderPositionsRequest(
  workOrderId: string,
): ListPositionRequest {
  return { workOrder: { active: true, values: [workOrderId] } };
}

/**
 * updateHtzWorkOrderPositionCache writes the position into the cached list
 * of the positions of its work order.
 */
export const updateHtzWorkOrderPositionCache = (position: Position) =>
  workOrderApi.util.updateQueryData(
    "htzWorkOrderListPosition",
    workOrderPositionsRequest(position.workOrderId),
    (draft) => {
      draft.items = draft.items.map((p) =>
        p.id === position.id ? position : p,
      );
    },
  );
